<template>
  <aside class="bl-pane">
    <!-- Header: visible count. Refresh + last-checked moved to the top bar's
         RefreshPlanButton — one obvious action beats two identical ones 200px
         apart, and a 28px icon in a side panel was too easy to miss. -->
    <div class="bl-header">
      <div class="bl-header-row">
        <div>
          <div class="bl-count"><strong>{{ visibleProcedures.length }}</strong> procedures</div>
          <div class="bl-meta" v-if="hiddenCount > 0">{{ hiddenCount }} hidden</div>
        </div>
      </div>

      <!-- Group-by + Hide controls -->
      <div class="bl-controls">
        <label class="bl-control-label">Group by</label>
        <select class="bl-select" :value="groupBy" @change="onGroupByChange($event.target.value)">
          <option value="priority">Priority</option>
          <option value="phase">Phase (CDT range)</option>
          <option value="quadrant">Quadrant</option>
          <option value="tooth">Tooth</option>
          <option value="none">No grouping</option>
        </select>
      </div>

      <div class="bl-controls" ref="hideControlsRoot">
        <label class="bl-control-label">Hide by priority</label>
        <button
          class="bl-multi-trigger"
          :class="{ active: hideMenuOpen }"
          @click="onToggleHideMenu"
        >
          <span v-if="hiddenPriorityDefNums.length === 0">All visible</span>
          <span v-else>{{ hiddenPriorityDefNums.length }} hidden</span>
          <i class="fa fa-caret-down"></i>
        </button>
      </div>
      <div v-if="hideMenuOpen" class="bl-multi-menu" ref="hideMenuRoot" @click.stop>
        <div v-if="priorities.length === 0" class="bl-multi-empty">No priorities defined for this practice.</div>
        <label v-for="p in priorities" :key="p.defNum" class="bl-multi-row">
          <input
            type="checkbox"
            :checked="hiddenPriorityDefNums.indexOf(p.defNum) !== -1"
            @change="toggleHiddenPriority(p.defNum)"
          />
          <span>{{ p.itemName }}</span>
          <span class="bl-multi-count">{{ countByPriority(p.defNum) }}</span>
        </label>
        <div class="bl-multi-actions">
          <button v-if="hiddenPriorityDefNums.length > 0" class="bl-multi-clear" @click="clearHidden">Clear hidden</button>
          <button class="bl-multi-done" @click="hideMenuOpen = false">Done</button>
        </div>
      </div>

      <!-- Show / hide diagnostic codes (exams & x-rays — D0xxx). Persisted
           per-patient; flows to Present Mode + the patient view. -->
      <label class="bl-checkrow">
        <input type="checkbox" :checked="showDiagnostic" @change="$emit('toggle-diagnostic')" />
        <span class="bl-checkrow-text">Show exams &amp; X-rays</span>
      </label>
    </div>

    <!-- Removed-from-this-plan strip. Pinned directly above the scrolling
         list (not inside it) so it's visible without scrolling to the bottom. -->
    <div v-if="removedProcedures.length > 0" class="bl-removed">
      <button class="bl-removed-toggle" @click="removedOpen = !removedOpen" :aria-expanded="removedOpen ? 'true' : 'false'">
        <i class="fa fa-chevron-right bl-removed-chevron" :class="{ open: removedOpen }"></i>
        <span class="bl-removed-title">Removed from this plan ({{ removedProcedures.length }})</span>
      </button>
      <div v-if="removedOpen" class="bl-removed-list bl-scroll">
        <div v-for="proc in removedProcedures" :key="proc.procNum" class="bl-removed-card">
          <div class="bl-removed-main">
            <div class="bl-removed-line">
              <span class="bl-removed-label">{{ procLabel(proc) }}</span>
              <span class="bl-tooth" v-if="proc.toothNum">#{{ proc.toothNum }}<span v-if="proc.surf"> · {{ proc.surf }}</span></span>
            </div>
            <div class="bl-removed-sub">
              <span class="bl-code">{{ proc.procCode }}</span>
              <span class="bl-removed-fee bl-tab-num">{{ formatMoney(proc.fee) }}</span>
            </div>
            <div class="bl-removed-meta">{{ removedMeta(proc) }}</div>
          </div>
          <button class="bl-removed-restore" @click="$emit('restore-procedure', proc)">Restore</button>
        </div>
      </div>
    </div>

    <!-- Procedure list (scrollable) -->
    <div class="bl-body bl-scroll">
      <!-- Loading skeleton -->
      <div v-if="loading && visibleProcedures.length === 0" class="bl-skel-list">
        <div v-for="n in 5" :key="n" class="bl-skel-row">
          <div class="bl-skel bl-skel-handle"></div>
          <div class="bl-skel bl-skel-text"></div>
          <div class="bl-skel bl-skel-fee"></div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="visibleProcedures.length === 0" class="bl-empty">
        <i class="fa fa-tooth"></i>
        <h4>No procedures to show</h4>
        <p v-if="totalProcedureCount === 0">No treatment-planned procedures for this plan in Open Dental.</p>
        <p v-else>All {{ totalProcedureCount }} procedures are hidden by your current filters.</p>
      </div>

      <!-- Grouped procedures -->
      <template v-else>
        <div v-for="group in groupedProcedures" :key="group.key" class="bl-group">
          <div class="bl-group-header" v-if="groupBy !== 'none'">
            <span class="bl-group-name">{{ group.label }}</span>
            <span class="bl-group-count">{{ group.items.length }}</span>
            <span class="bl-group-total bl-tab-num">{{ formatMoney(group.total) }}</span>
          </div>

          <draggable
            :list="group.items"
            :group="{ name: 'procs', pull: groupBy === 'none', put: groupBy === 'none' }"
            handle=".bl-handle"
            ghost-class="bl-row-ghost"
            chosen-class="bl-row-chosen"
            drag-class="bl-row-drag"
            :animation="160"
            @end="onDragEnd"
          >
            <div
              v-for="proc in group.items"
              :key="proc.procNum"
              class="bl-row-stack"
            >
              <div
                class="bl-row"
                :class="{
                  'bl-row-scheduled': proc.isScheduled,
                  'bl-row-primary': hasAlts(proc),
                  'bl-row-new': isNew(proc),
                }"
                @click="onRowClick(proc)"
              >
                <span class="bl-handle" title="Drag to reorder">
                  <i class="fa fa-bars"></i>
                </span>
                <div class="bl-row-main">
                  <div class="bl-row-line">
                    <span class="bl-row-label">{{ procLabel(proc) }}</span>
                    <span class="bl-tooth" v-if="proc.toothNum">#{{ proc.toothNum }}<span v-if="proc.surf"> · {{ proc.surf }}</span></span>
                  </div>
                  <div class="bl-row-sub">
                    <span class="bl-code">{{ proc.procCode }}</span>
                    <span v-if="isNew(proc)" class="bl-pill bl-pill-new">New</span>
                    <span v-if="proc.priorityName" class="bl-pill bl-pill-priority">{{ proc.priorityName }}</span>
                    <span v-if="proc.providerAbbr" class="bl-pill">{{ proc.providerAbbr }}</span>
                    <span v-if="proc.isScheduled" class="bl-pill bl-pill-warn">Scheduled</span>
                    <span v-if="hasAlts(proc)" class="bl-pill bl-pill-alt">{{ altCountFor(proc) }} alt{{ altCountFor(proc) === 1 ? '' : 's' }}</span>
                  </div>
                </div>
                <div class="bl-row-fee bl-tab-num">{{ formatMoney(proc.fee) }}</div>
                <div class="bl-row-actions">
                  <button class="bl-row-alt" @click="$emit('open-alt-picker', proc)" title="Attach alternatives">
                    <i class="fa fa-plus"></i> alt
                  </button>
                  <button class="bl-row-remove" @click="$emit('remove-procedure', proc)" title="Remove from this plan">
                    <i class="fa fa-trash-o"></i>
                  </button>
                </div>
              </div>

              <!-- Nested alt rows -->
              <div v-if="hasAlts(proc)" class="bl-alt-stack">
                <div
                  v-for="(alt, altIdx) in altsByPrimary[proc.procNum]"
                  :key="alt.procNum"
                  class="bl-alt-row"
                >
                  <span class="bl-or-divider">
                    <span class="bl-or-line"></span>
                    <span class="bl-or-pill">{{ altIdx === 0 ? 'or' : 'or also' }}</span>
                  </span>
                  <div class="bl-row bl-row-nested">
                    <span class="bl-handle bl-handle-disabled" title="Alts can't be reordered in Phase 1">
                      <i class="fa fa-bars"></i>
                    </span>
                    <div class="bl-row-main">
                      <div class="bl-row-line">
                        <span class="bl-row-label">{{ procLabel(alt) }}</span>
                        <span class="bl-tooth" v-if="alt.toothNum">#{{ alt.toothNum }}<span v-if="alt.surf"> · {{ alt.surf }}</span></span>
                      </div>
                      <div class="bl-row-sub">
                        <span class="bl-code">{{ alt.procCode }}</span>
                        <span v-if="alt.priorityName" class="bl-pill bl-pill-priority">{{ alt.priorityName }}</span>
                        <span v-if="alt.providerAbbr" class="bl-pill">{{ alt.providerAbbr }}</span>
                      </div>
                    </div>
                    <div class="bl-row-fee bl-tab-num">{{ formatMoney(alt.fee) }}</div>
                    <button
                      class="bl-row-detach"
                      @click="$emit('detach-alt', alt)"
                      title="Detach this alternative"
                    >
                      <i class="fa fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </draggable>
        </div>
      </template>
    </div>

    <!-- Footer total -->
    <div v-if="visibleProcedures.length > 0" class="bl-footer">
      <span class="bl-footer-label">Visible total</span>
      <span class="bl-footer-value bl-tab-num">{{ formatMoney(visibleTotal) }}</span>
    </div>
  </aside>
</template>

<script>
import draggable from 'vuedraggable';

// Grouping lives in utils/groupProcedures.js so this pane and the Builder's
// Reasons tab group identically — see that file's header for why.
import { groupProcedures } from '../utils/groupProcedures.js';

export default {
  name: 'BuilderLeftPane',
  components: { draggable },

  props: {
    loading: { type: Boolean, default: false },
    procedures: { type: Array, default: () => [] },
    visibleProcedures: { type: Array, default: () => [] },
    priorities: { type: Array, default: () => [] },
    showDiagnostic: { type: Boolean, default: true },
    hiddenPriorityDefNums: { type: Array, default: () => [] },
    groupBy: { type: String, default: 'priority' },
    // ProcNums that arrived in the most recent refresh-from-OD. Badged until
    // the TC clicks the row — a new procedure sorts to the BOTTOM of its group
    // (it has no entry in proceduresOrder), so without a marker it lands
    // off-screen on a long plan and the refresh looks like it did nothing.
    newProcNums: { type: Array, default: () => [] },
    altsByPrimary: { type: Object, default: () => ({}) },
    removedProcedures: { type: Array, default: () => [] },
  },

  data() {
    return {
      hideMenuOpen: false,
      removedOpen: false,
      // Bound click listener kept on the instance so beforeDestroy can
      // remove the exact reference it added.
      onDocClickForHideMenu: null,
    };
  },

  mounted() {
    // Click-outside-to-close for the Hide-By-Priority dropdown. Bound here
    // (not in onToggleHideMenu) so the handler reference is stable across
    // open/close cycles — easier to teardown. Document-level check rather
    // than per-element so it survives v-if re-mounts of the menu node.
    this.onDocClickForHideMenu = (e) => {
      if (!this.hideMenuOpen) return;
      const menu = this.$refs.hideMenuRoot;
      const trigger = this.$refs.hideControlsRoot;
      if (menu && menu.contains(e.target)) return;
      if (trigger && trigger.contains(e.target)) return;
      this.hideMenuOpen = false;
    };
    document.addEventListener('mousedown', this.onDocClickForHideMenu);
  },

  beforeDestroy() {
    if (this.onDocClickForHideMenu) {
      document.removeEventListener('mousedown', this.onDocClickForHideMenu);
      this.onDocClickForHideMenu = null;
    }
  },

  computed: {
    totalProcedureCount() { return this.procedures.length; },
    hiddenCount() { return this.totalProcedureCount - this.visibleProcedures.length - this.removedProcedures.length; },
    visibleTotal() {
      return this.visibleProcedures.reduce((s, p) => s + (p.fee || 0), 0);
    },

    groupedProcedures() {
      return groupProcedures(this.visibleProcedures, this.groupBy, this.priorities);
    },
  },

  methods: {
    onToggleHideMenu() {
      this.hideMenuOpen = !this.hideMenuOpen;
    },

    hasAlts(proc) {
      const alts = this.altsByPrimary[proc.procNum];
      return Array.isArray(alts) && alts.length > 0;
    },
    altCountFor(proc) {
      const alts = this.altsByPrimary[proc.procNum];
      return Array.isArray(alts) ? alts.length : 0;
    },

    procLabel(proc) {
      // Prefer AbbrDesc when available (shorter, clinic-edited), fallback to Descript
      return proc.abbrDesc || proc.descript || proc.procCode || '—';
    },

    removedMeta(proc) {
      let when = '';
      if (proc.removedAt) {
        const d = new Date(proc.removedAt);
        if (!isNaN(d.getTime())) {
          const now = new Date();
          const sameDay = d.getFullYear() === now.getFullYear()
            && d.getMonth() === now.getMonth()
            && d.getDate() === now.getDate();
          when = sameDay ? 'today' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
      }
      const by = proc.removedByName;
      let s = 'Removed';
      if (when) s += ` ${when}`;
      if (by) s += ` by ${by}`;
      s += ' · still in Open Dental';
      return s;
    },

    countByPriority(defNum) {
      return this.procedures.filter((p) => p.priority === defNum).length;
    },

    toggleHiddenPriority(defNum) {
      const next = [...this.hiddenPriorityDefNums];
      const idx = next.indexOf(defNum);
      if (idx === -1) next.push(defNum);
      else next.splice(idx, 1);
      this.$emit('set-hidden-priorities', next);
    },

    clearHidden() {
      this.$emit('set-hidden-priorities', []);
      this.hideMenuOpen = false;
    },

    onGroupByChange(mode) {
      this.$emit('set-group-by', mode);
    },

    onDragEnd() {
      // After draggable mutates the group's items in place, walk the rendered
      // groups in display order to compose a flat ProcNum sequence and emit it.
      // Hidden procedures are appended at the end so they don't lose their slot
      // when re-shown — but their relative order is preserved.
      const visibleSequence = [];
      this.groupedProcedures.forEach((g) => {
        g.items.forEach((p) => visibleSequence.push(p.procNum));
      });

      const hiddenSequence = this.procedures
        .filter((p) => !visibleSequence.includes(p.procNum))
        .map((p) => p.procNum);

      this.$emit('reorder', [...visibleSequence, ...hiddenSequence]);
    },

    formatMoney(n) {
      if (n === null || n === undefined) return '—';
      const num = parseFloat(n) || 0;
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    isNew(proc) {
      return this.newProcNums.indexOf(proc.procNum) !== -1;
    },

    /**
     * Clicking a badged row is the acknowledgement — the TC has now seen it.
     * No-op on every other row, so this doesn't add a click target where the
     * pane previously had none.
     */
    onRowClick(proc) {
      if (this.isNew(proc)) this.$emit('acknowledge-new', proc.procNum);
    },
  },
};
</script>

<style scoped>
.bl-pane {
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-right: 1px solid #e4e7ec;
  min-width: 0;
  min-height: 0;

  --bl-text: #0f172a;
  --bl-text-mute: #475569;
  --bl-text-dim: #94a3b8;
  --bl-border: #e4e7ec;
  --bl-border-strong: #cdd3dc;
  --bl-surface: #ffffff;
  --bl-surface2: #f1f3f6;
  --bl-bg: #f7f8fa;
  --bl-accent: #0f6e63;
  --bl-accent-soft: #e6f1ef;
  --bl-warn: #b45309;
  --bl-warn-soft: #fef3c7;

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 13px;
  color: var(--bl-text);
  -webkit-font-smoothing: antialiased;
}
.bl-pane * { box-sizing: border-box; }
.bl-tab-num { font-variant-numeric: tabular-nums; }

.bl-header {
  padding: 12px 14px 10px;
  border-bottom: 1px solid var(--bl-border);
  background: var(--bl-surface);
}
.bl-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.bl-count { font-size: 14px; color: var(--bl-text); }
.bl-count strong { font-weight: 600; }
.bl-meta { font-size: 11px; color: var(--bl-text-dim); margin-top: 2px; }

.bl-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.bl-checkrow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 10px 0 0;
  cursor: pointer;
}
.bl-checkrow input[type="checkbox"] {
  width: 14px;
  height: 14px;
  accent-color: var(--bl-accent, #0d5f54);
  cursor: pointer;
  flex-shrink: 0;
}
.bl-checkrow-text {
  font-size: 12px;
  color: var(--bl-text, #334155);
}
.bl-control-label {
  font-size: 11px;
  color: var(--bl-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  width: 88px;
  flex-shrink: 0;
  margin: 0;
}
.bl-select,
.bl-multi-trigger {
  flex: 1;
  box-sizing: border-box;
  /* Explicit min-height so font + padding can't get squeezed by parent
     flex constraints. 32px = 12px font + 1.3 line-height (15.6) + ~16px
     vertical padding. */
  min-height: 32px;
  padding: 7px 10px;
  border: 1px solid var(--bl-border);
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.3;
  background: var(--bl-surface);
  color: var(--bl-text);
  cursor: pointer;
  min-width: 0;
}
/* `<select>` only — strip native OS chrome (which was clipping descenders
   on the trailing letter of "Priority" / "Phase (CDT range)") and draw our
   own chevron via inline SVG background. Bulletproof across Chrome / Edge /
   Safari / Firefox. */
.bl-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 28px;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2010%206%22%3E%3Cpath%20fill%3D%22%23475569%22%20d%3D%22M0%200l5%206%205-6z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 10px 6px;
  text-overflow: ellipsis;
}
.bl-select::-ms-expand { display: none; } /* IE/old Edge */
.bl-select:focus,
.bl-multi-trigger:focus,
.bl-multi-trigger.active { border-color: var(--bl-accent); outline: none; }
.bl-multi-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
}

.bl-multi-menu {
  margin-top: 6px;
  padding: 6px;
  border: 1px solid var(--bl-border);
  border-radius: 8px;
  background: var(--bl-surface);
  box-shadow: 0 4px 12px rgba(15,23,42,0.08);
  max-height: 240px;
  overflow: auto;
}
.bl-multi-empty {
  padding: 10px;
  font-size: 12px;
  color: var(--bl-text-dim);
}
.bl-multi-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}
.bl-multi-row:hover { background: var(--bl-surface2); }
.bl-multi-count {
  margin-left: auto;
  font-size: 11px;
  color: var(--bl-text-dim);
}
/* Action row at the bottom of the Hide-By-Priority menu — Clear + Done.
   Done is discoverable for touch users where click-outside isn't intuitive. */
.bl-multi-actions {
  display: flex;
  gap: 6px;
  margin-top: 4px;
  border-top: 1px solid var(--bl-border);
  padding-top: 6px;
}
.bl-multi-done {
  flex: 1;
  padding: 6px;
  background: var(--bl-accent);
  border: 1px solid var(--bl-accent);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
}
.bl-multi-done:hover { background: #094840; border-color: #094840; }
.bl-multi-clear {
  flex: 1;
  padding: 6px;
  background: transparent;
  border: 1px solid var(--bl-border);
  border-radius: 6px;
  font-size: 12px;
  color: var(--bl-accent);
  cursor: pointer;
  text-align: center;
}
.bl-multi-clear:hover { background: var(--bl-surface2); }

/* List body */
.bl-body {
  flex: 1;
  overflow-y: auto;
  background: var(--bl-bg);
}
.bl-scroll::-webkit-scrollbar { width: 8px; }
.bl-scroll::-webkit-scrollbar-thumb { background: var(--bl-border-strong); border-radius: 4px; }
.bl-scroll::-webkit-scrollbar-track { background: transparent; }

.bl-group { padding: 0 0 6px; }
.bl-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px 4px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--bl-text-dim);
  position: sticky;
  top: 0;
  background: var(--bl-bg);
  z-index: 1;
}
.bl-group-name { font-weight: 600; color: var(--bl-text-mute); letter-spacing: 0.4px; }
.bl-group-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 16px;
  padding: 0 5px;
  background: var(--bl-surface2);
  border-radius: 999px;
  font-size: 10px;
  color: var(--bl-text-mute);
}
.bl-group-total { margin-left: auto; font-size: 11px; color: var(--bl-text-mute); font-weight: 600; }

.bl-row {
  display: grid;
  grid-template-columns: 18px 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  margin: 0 8px 4px;
  background: var(--bl-surface);
  border: 1px solid var(--bl-border);
  border-radius: 8px;
  cursor: default;
  transition: border-color 0.1s, box-shadow 0.1s;
}
.bl-row:hover { border-color: var(--bl-border-strong); box-shadow: 0 1px 3px rgba(15,23,42,0.06); }
.bl-row-scheduled { background: #fff7ed; border-color: #fed7aa; }

/* Just arrived from Open Dental. Accented rather than warning-coloured —
   nothing is wrong, there's just something here the TC hasn't seen. The row
   sits at the bottom of its group (no proceduresOrder entry yet), so this is
   often the only thing that makes a successful refresh visible. Clicking the
   row clears it. Placed after .bl-row-scheduled so a new-and-scheduled
   procedure reads as new — that's the fact the TC doesn't already know. */
.bl-row-new {
  background: var(--bl-accent-soft, #e6f1ef);
  border-color: var(--bl-accent, #0f6e63);
  cursor: pointer;
  animation: bl-new-flash 1.2s ease-out 1;
}
@keyframes bl-new-flash {
  0%   { box-shadow: 0 0 0 0 rgba(15, 110, 99, 0.35); }
  100% { box-shadow: 0 0 0 6px rgba(15, 110, 99, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .bl-row-new { animation: none; }
}

.bl-handle {
  cursor: grab;
  color: var(--bl-text-dim);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.bl-handle:active { cursor: grabbing; }

.bl-row-main { min-width: 0; }
.bl-row-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 2px;
}
.bl-row-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--bl-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bl-tooth {
  font-size: 11px;
  color: var(--bl-text-mute);
  flex-shrink: 0;
}
.bl-row-sub {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 11px;
  color: var(--bl-text-dim);
}
.bl-code {
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}
.bl-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--bl-surface2);
  color: var(--bl-text-mute);
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2px;
}
.bl-pill-priority { background: var(--bl-accent-soft); color: var(--bl-accent); }
.bl-pill-warn { background: var(--bl-warn-soft); color: var(--bl-warn); }
/* Solid, unlike the other pills — this one has to win attention against a
   priority pill sitting right beside it on the same row. */
.bl-pill-new {
  background: var(--bl-accent);
  color: #ffffff;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.bl-row-fee {
  font-size: 13px;
  font-weight: 600;
  color: var(--bl-text);
  text-align: right;
}
.bl-row-alt {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 11px;
  color: var(--bl-text-mute);
  background: transparent;
  border: 1px dashed var(--bl-border-strong);
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s, color 0.1s;
}
.bl-row-alt:hover {
  background: var(--bl-accent-soft);
  border-color: var(--bl-accent);
  color: var(--bl-accent);
}

.bl-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.bl-row-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  font-size: 11px;
  color: var(--bl-text-mute);
  background: transparent;
  border: 1px dashed var(--bl-border-strong);
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s, color 0.1s;
}
.bl-row-remove:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #b91c1c;
}

/* Alt-row stack: a primary row with its alts indented underneath */
.bl-row-stack { margin-bottom: 4px; }
.bl-row-primary {
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}
.bl-alt-stack {
  margin: 0 8px 0 28px;
  padding-bottom: 4px;
}
.bl-alt-row { padding-top: 4px; }

.bl-or-divider {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-left: 4px;
  margin: 4px 0 4px;
}
.bl-or-line {
  flex: 1;
  height: 1px;
  background: var(--bl-border);
}
.bl-or-pill {
  padding: 1px 8px;
  background: var(--bl-surface);
  border: 1px solid var(--bl-border);
  border-radius: 999px;
  font-size: 9.5px;
  color: var(--bl-text-mute);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.bl-row-nested {
  margin: 0;
  background: var(--bl-surface);
  border-color: var(--bl-border);
}
.bl-row-nested .bl-row-label { font-weight: 500; }

.bl-handle-disabled {
  cursor: not-allowed;
  opacity: 0.35;
}

.bl-row-detach {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  font-size: 11px;
  color: var(--bl-text-dim);
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.1s, color 0.1s, border-color 0.1s;
}
.bl-row-detach:hover {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
}

.bl-pill-alt {
  background: #fef3c7 !important;
  color: #b45309 !important;
}

/* Drag states */
.bl-row-ghost { opacity: 0.4; background: var(--bl-accent-soft); }
.bl-row-chosen { box-shadow: 0 4px 12px rgba(15,23,42,0.12); }
.bl-row-drag { opacity: 0.9; }

/* Empty / skeleton */
.bl-empty {
  text-align: center;
  padding: 36px 20px;
  color: var(--bl-text-mute);
}
.bl-empty .fa { font-size: 24px; color: var(--bl-text-dim); margin-bottom: 8px; }
.bl-empty h4 { font-size: 14px; font-weight: 600; margin-bottom: 4px; color: var(--bl-text); }
.bl-empty p { font-size: 12px; color: var(--bl-text-mute); }

.bl-skel-list { padding: 12px; }
.bl-skel-row {
  display: grid;
  grid-template-columns: 16px 1fr 60px;
  gap: 10px;
  padding: 10px 0;
  align-items: center;
}
.bl-skel {
  background: linear-gradient(90deg, #eef0f3 25%, #e4e7ec 37%, #eef0f3 63%);
  background-size: 400% 100%;
  animation: bl-shimmer 1.4s ease infinite;
  border-radius: 4px;
}
.bl-skel-handle { height: 12px; }
.bl-skel-text { height: 14px; }
.bl-skel-fee { height: 14px; }
@keyframes bl-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

/* Removed-from-this-plan strip. Sits between the header zone and the
   scrolling list — a tinted surface + bottom border set it apart from the
   header controls above and the procedure cards below. */
.bl-removed {
  border-bottom: 1px solid var(--bl-border);
  background: var(--bl-surface2);
  flex-shrink: 0;
}
.bl-removed-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--bl-text-mute);
  text-align: left;
}
.bl-removed-toggle:hover { background: rgba(15, 23, 42, 0.05); }
.bl-removed-chevron {
  font-size: 10px;
  color: var(--bl-text-dim);
  transition: transform 0.12s ease;
}
.bl-removed-chevron.open { transform: rotate(90deg); }
.bl-removed-title { font-weight: 600; }

.bl-removed-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 4px 8px 8px;
  background: var(--bl-bg);
  border-top: 1px solid var(--bl-border);
}
.bl-removed-card {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  margin: 4px 0 0;
  background: var(--bl-surface);
  border: 1px solid var(--bl-border);
  border-radius: 8px;
  opacity: 0.72;
}
.bl-removed-main { min-width: 0; }
.bl-removed-line {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 2px;
}
.bl-removed-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--bl-text-mute);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: line-through;
}
.bl-removed-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--bl-text-dim);
}
.bl-removed-fee { font-weight: 600; }
.bl-removed-meta {
  margin-top: 3px;
  font-size: 10.5px;
  color: var(--bl-text-dim);
}
.bl-removed-restore {
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  color: var(--bl-accent);
  background: transparent;
  border: 1px solid var(--bl-border-strong);
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s, color 0.1s;
}
.bl-removed-restore:hover {
  background: var(--bl-accent-soft);
  border-color: var(--bl-accent);
  color: var(--bl-accent);
}

/* Footer */
.bl-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-top: 1px solid var(--bl-border);
  background: var(--bl-surface);
}
.bl-footer-label { font-size: 11px; color: var(--bl-text-dim); text-transform: uppercase; letter-spacing: 0.4px; }
.bl-footer-value { font-size: 14px; font-weight: 600; }
</style>
